import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  Loader2,
  Check,
  KeyRound,
  Car,
  Copy,
  Share2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { getReferralCodeFromUrl, buildReferralUrl } from "@/lib/referral";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ReferralPanel({ code, dark, testIdPrefix }) {
  const [pos, setPos] = useState(null);
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => buildReferralUrl(code), [code]);

  useEffect(() => {
    let cancelled = false;
    const fetchPos = async () => {
      try {
        const { data } = await axios.get(`${API}/waitlist/me/${code}`);
        if (!cancelled) setPos(data);
      } catch {
        /* silent */
      }
    };
    fetchPos();
    const t = setInterval(fetchPos, 12000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [code]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied", { description: "Share it anywhere." });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy", { description: "Long-press to copy manually." });
    }
  };

  const share = async () => {
    const shareData = {
      title: "CARVNY — Dubai's peer-to-peer car rental",
      text: "I just joined the CARVNY waitlist. Use my link to skip the line:",
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        /* user cancelled — fall through to copy */
      }
    }
    copy();
  };

  const wa = `https://wa.me/?text=${encodeURIComponent(
    `I just joined the CARVNY waitlist — Dubai's peer-to-peer car rental marketplace. Skip the line with my link: ${url}`
  )}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `Just joined the @carvny waitlist — Dubai's peer-to-peer car rental marketplace. Skip the line:`
  )}&url=${encodeURIComponent(url)}`;

  const bgChip = dark
    ? "bg-white/[0.06] border border-white/15"
    : "bg-carvny-red-50 border border-carvny-red/15";
  const inputBg = dark
    ? "bg-white/[0.05] border border-white/15 text-white"
    : "bg-white border border-carvny-border text-carvny-ink";
  const mutedText = dark ? "text-white/65" : "text-carvny-muted";
  const subtleBtn = dark
    ? "bg-white/[0.06] border border-white/15 text-white hover:bg-white/[0.12]"
    : "bg-white border border-carvny-border text-carvny-ink hover:bg-carvny-surface";

  return (
    <div data-testid={`${testIdPrefix}-referral-panel`} className="mt-8">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-full inline-flex ${bgChip}`}>
        <Sparkles size={14} className="text-carvny-red" />
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-carvny-red">
          Skip the line
        </span>
      </div>

      {/* Position card */}
      <div
        className={`mt-4 grid grid-cols-3 gap-px rounded-2xl overflow-hidden ${
          dark ? "bg-white/10" : "bg-carvny-border"
        }`}
      >
        <PosCell
          dark={dark}
          label="Your position"
          value={pos ? `#${pos.position.toLocaleString()}` : "—"}
          testid={`${testIdPrefix}-pos`}
        />
        <PosCell
          dark={dark}
          label="Referrals"
          value={pos ? pos.referrals.toLocaleString() : "0"}
          accent
          testid={`${testIdPrefix}-referrals`}
        />
        <PosCell
          dark={dark}
          label="Spots jumped"
          value={pos ? pos.bonus_spots.toLocaleString() : "0"}
          testid={`${testIdPrefix}-bonus`}
        />
      </div>

      <p className={`mt-4 text-[13px] leading-relaxed ${mutedText}`}>
        Refer a friend and jump <strong className={dark ? "text-white" : "text-carvny-ink"}>10 spots</strong> closer
        to launch day for every signup. Your link is yours forever.
      </p>

      {/* Link + copy */}
      <div className="mt-4 flex items-stretch gap-2">
        <input
          readOnly
          value={url}
          data-testid={`${testIdPrefix}-share-link`}
          onFocus={(e) => e.target.select()}
          className={`flex-1 h-12 px-4 rounded-full text-sm font-mono ${inputBg} outline-none focus-ring`}
        />
        <button
          onClick={copy}
          data-testid={`${testIdPrefix}-copy-link`}
          className={`shrink-0 h-12 inline-flex items-center justify-center gap-2 px-5 rounded-full text-sm font-bold transition-colors ${
            copied
              ? "bg-carvny-red text-white"
              : "bg-carvny-red text-white hover:bg-carvny-red-hover"
          }`}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>

      {/* Share row */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={share}
          data-testid={`${testIdPrefix}-share-native`}
          className={`h-11 inline-flex items-center gap-2 px-4 rounded-full text-[13px] font-bold transition-colors ${subtleBtn}`}
        >
          <Share2 size={14} />
          Share
        </button>
        <a
          data-testid={`${testIdPrefix}-share-whatsapp`}
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className={`h-11 inline-flex items-center px-4 rounded-full text-[13px] font-bold transition-colors ${subtleBtn}`}
        >
          WhatsApp
        </a>
        <a
          data-testid={`${testIdPrefix}-share-twitter`}
          href={tw}
          target="_blank"
          rel="noopener noreferrer"
          className={`h-11 inline-flex items-center px-4 rounded-full text-[13px] font-bold transition-colors ${subtleBtn}`}
        >
          X / Twitter
        </a>
      </div>
    </div>
  );
}

function PosCell({ label, value, accent, dark, testid }) {
  return (
    <div
      data-testid={testid}
      className={`p-4 sm:p-5 ${
        dark ? "bg-carvny-ink" : "bg-white"
      }`}
    >
      <div
        className={`text-[10px] font-bold uppercase tracking-[0.22em] ${
          dark ? "text-white/55" : "text-carvny-muted"
        }`}
      >
        {label}
      </div>
      <div
        className={`mt-2 font-display text-2xl sm:text-3xl font-black leading-none tabular-nums ${
          accent ? "text-carvny-red" : dark ? "text-white" : "text-carvny-ink"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function WaitlistForm({
  role,
  eyebrow,
  title,
  accent,
  sub,
  ctaLabel,
  icon: Icon,
  dark = false,
  testIdPrefix,
}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [referralCode, setReferralCode] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const value = email.trim();
    if (!EMAIL_REGEX.test(value)) {
      setStatus("error");
      setErrorMsg("Enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      const referredBy = getReferralCodeFromUrl();
      const { data } = await axios.post(`${API}/waitlist`, {
        email: value,
        role,
        source: "landing_page",
        ...(referredBy ? { referred_by: referredBy } : {}),
      });
      setReferralCode(data.referral_code);
      setSubmittedEmail(value);
      setStatus("success");
      toast.success(
        role === "host" ? "You're on the host list." : "You're on the renter list.",
        { description: "Check your inbox + share your link to skip ahead." }
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again.";
      setErrorMsg(msg);
      setStatus("error");
      toast.error("Couldn't join the waitlist", { description: msg });
    }
  };

  const containerBg = dark
    ? "bg-carvny-ink text-white border border-white/[0.06]"
    : "bg-white text-carvny-ink border border-carvny-border";
  const subColor = dark ? "text-white/65" : "text-carvny-muted";
  const inputClass = dark
    ? "bg-white/[0.05] border border-white/15 text-white placeholder:text-white/40 focus:bg-white/[0.08]"
    : "bg-white border border-carvny-border text-carvny-ink placeholder:text-carvny-muted/70";

  return (
    <div
      data-testid={`${testIdPrefix}-card`}
      className={`relative ${containerBg} rounded-[28px] p-8 sm:p-12 overflow-hidden`}
    >
      {dark && (
        <>
          <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-carvny-red/40 blur-3xl pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        </>
      )}
      <div className="relative">
        {/* Editorial top row */}
        <div className="flex items-center justify-between">
          <span
            className={`text-[11px] font-bold uppercase tracking-[0.28em] ${
              dark ? "text-white/70" : "text-carvny-red"
            }`}
          >
            {eyebrow}
          </span>
          <div
            className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
              dark ? "bg-white text-carvny-red" : "bg-carvny-red text-white"
            }`}
          >
            <Icon size={20} strokeWidth={2.2} />
          </div>
        </div>
        <div
          className={`mt-5 h-px ${
            dark
              ? "bg-gradient-to-r from-transparent via-white/15 to-transparent"
              : "bg-gradient-to-r from-transparent via-carvny-border to-transparent"
          }`}
        />

        <h3 className="font-display mt-7 text-[32px] sm:text-[40px] font-black tracking-tight leading-[1.02]">
          {title}{" "}
          <span
            className={`font-serif-italic font-normal ${
              dark ? "text-white/85" : "text-carvny-ink/85"
            }`}
          >
            {accent}
          </span>
        </h3>
        <p className={`mt-4 text-[15px] sm:text-base leading-relaxed ${subColor} max-w-md`}>
          {sub}
        </p>

        <form onSubmit={submit} noValidate className="mt-8 flex flex-col gap-3">
          <label htmlFor={`${testIdPrefix}-email`} className="sr-only">
            Email address
          </label>
          <input
            id={`${testIdPrefix}-email`}
            data-testid={`${testIdPrefix}-email-input`}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === "error") setStatus("idle");
            }}
            disabled={status === "loading" || status === "success"}
            className={`w-full h-14 px-5 rounded-full text-base font-medium ${inputClass} outline-none focus-ring transition-all`}
          />
          <button
            type="submit"
            data-testid={`${testIdPrefix}-submit`}
            disabled={status === "loading" || status === "success"}
            className={`group h-14 inline-flex items-center justify-center gap-2 rounded-full px-6 text-base font-bold transition-all ${
              status === "success"
                ? "bg-carvny-red/15 text-carvny-red cursor-default"
                : "bg-carvny-red text-white hover:bg-carvny-red-hover shadow-[0_18px_38px_-14px_rgba(204,0,0,0.55)]"
            } disabled:opacity-90`}
          >
            {status === "loading" && (
              <>
                <Loader2 size={18} className="animate-spin" /> Joining…
              </>
            )}
            {status === "success" && (
              <>
                <Check size={18} /> You&apos;re on the list
              </>
            )}
            {(status === "idle" || status === "error") && (
              <>
                {ctaLabel}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
          {status === "error" && errorMsg && (
            <p
              data-testid={`${testIdPrefix}-error`}
              className={`text-sm font-semibold ${dark ? "text-white" : "text-carvny-red"}`}
            >
              {errorMsg}
            </p>
          )}
          {status === "success" && (
            <p
              data-testid={`${testIdPrefix}-success`}
              className={`text-sm font-semibold ${dark ? "text-white/80" : "text-carvny-muted"}`}
            >
              We&apos;ll email you at{" "}
              <span className="text-carvny-red">{submittedEmail}</span> when we launch.
            </p>
          )}
        </form>

        {status === "success" && referralCode && (
          <ReferralPanel code={referralCode} dark={dark} testIdPrefix={testIdPrefix} />
        )}

        {status !== "success" && (
          <div
            className={`mt-6 text-[11px] uppercase tracking-[0.24em] font-bold ${
              dark ? "text-white/40" : "text-carvny-muted"
            }`}
          >
            No spam · Unsubscribe anytime
          </div>
        )}
      </div>
    </div>
  );
}

export default function Waitlist() {
  const [refFromUrl, setRefFromUrl] = useState(null);
  useEffect(() => {
    setRefFromUrl(getReferralCodeFromUrl());
  }, []);

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-carvny-surface border-t border-carvny-border overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial header */}
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
              §05 — Join the waitlist
            </div>
            <div className="rule mt-4" />
            {refFromUrl && (
              <div
                data-testid="waitlist-referral-banner"
                className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-carvny-red-50 border border-carvny-red/20"
              >
                <Sparkles size={14} className="text-carvny-red" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-carvny-red">
                  Invited by a friend · skip-the-line credit applied
                </span>
              </div>
            )}
          </div>
          <div className="col-span-12 lg:col-span-8">
            <h2
              data-testid="waitlist-headline"
              className="font-display text-[44px] sm:text-6xl lg:text-[88px] font-black tracking-tightest text-carvny-ink leading-[0.98]"
            >
              CARVNY is coming
              <br />
              to{" "}
              <span className="font-serif-italic font-normal text-carvny-ink/85">
                Dubai
              </span>
              <span className="text-carvny-red">.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-carvny-muted leading-relaxed max-w-2xl">
              Be first when we launch. Whether you have a car to put to work or
              keys to find for your next trip — your seat is here.
            </p>
          </div>
        </div>

        {/* Forms */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          <WaitlistForm
            testIdPrefix="waitlist-host"
            role="host"
            eyebrow="I want to list my car"
            title="Earn from the hours your car"
            accent="already sits idle."
            sub="Join the host list to be first onboard. We'll personally walk you through your first listing."
            ctaLabel="Host Your Car"
            icon={KeyRound}
            dark={true}
          />
          <WaitlistForm
            testIdPrefix="waitlist-renter"
            role="renter"
            eyebrow="I want to rent a car"
            title="Drive Dubai without"
            accent="the deposit drama."
            sub="Be first when cars go live near you — transparent pricing, zero surprise fines."
            ctaLabel="Rent a Car"
            icon={Car}
          />
        </div>
      </div>
    </section>
  );
}
